export class GameAudio {
  private context: AudioContext | null = null;
  private engineOscillator: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private muted = false;

  async unlock(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.engineOscillator = this.context.createOscillator();
      this.engineGain = this.context.createGain();
      this.engineOscillator.type = "sawtooth";
      this.engineGain.gain.value = 0;
      this.engineOscillator.connect(this.engineGain).connect(this.context.destination);
      this.engineOscillator.start();
    }
    if (this.context.state === "suspended") await this.context.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.engineGain && this.context) this.engineGain.gain.setTargetAtTime(0, this.context.currentTime, 0.03);
  }

  isMuted(): boolean {
    return this.muted;
  }

  countdown(label: "3" | "2" | "1" | "GO"): void {
    if (!this.context || this.muted) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = label === "GO" ? "square" : "sine";
    const frequencies = { "3": 440, "2": 500, "1": 590, GO: 880 };
    oscillator.frequency.setValueAtTime(frequencies[label], this.context.currentTime);
    if (label === "GO") oscillator.frequency.exponentialRampToValueAtTime(1_180, this.context.currentTime + 0.22);
    gain.gain.setValueAtTime(label === "GO" ? 0.22 : 0.16, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + (label === "GO" ? 0.52 : 0.24));
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start();
    oscillator.stop(this.context.currentTime + 0.55);
  }

  updateEngine(speed: number, baseTone: number, throttle: number, boosting: boolean): void {
    if (!this.context || !this.engineOscillator || !this.engineGain) return;
    const now = this.context.currentTime;
    const targetFrequency = baseTone + speed * 5.5 + (boosting ? 90 : 0);
    this.engineOscillator.frequency.setTargetAtTime(Math.max(45, targetFrequency), now, 0.045);
    const volume = this.muted ? 0 : 0.015 + Math.min(0.08, speed / 900) + throttle * 0.02;
    this.engineGain.gain.setTargetAtTime(volume, now, 0.06);
  }

  effect(kind: "boost" | "jump" | "drift"): void {
    if (!this.context || this.muted) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = kind === "drift" ? "sawtooth" : "triangle";
    const start = kind === "boost" ? 220 : kind === "jump" ? 310 : 130;
    const end = kind === "boost" ? 660 : kind === "jump" ? 520 : 85;
    oscillator.frequency.setValueAtTime(start, this.context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(end, this.context.currentTime + 0.18);
    gain.gain.setValueAtTime(0.07, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + 0.22);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start();
    oscillator.stop(this.context.currentTime + 0.24);
  }

  silenceEngine(): void {
    if (this.engineGain && this.context) this.engineGain.gain.setTargetAtTime(0, this.context.currentTime, 0.02);
  }

  dispose(): void {
    this.engineOscillator?.stop();
    void this.context?.close();
    this.context = null;
  }
}
